import { useState } from 'react'

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react'
import { useAuthenticatedAccount } from 'src/features/auth/context'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'

import { useWithdrawWorksheet } from '../../api/withdrawWorksheet'
import { useWorksheetValueContext } from '../../context/WorksheetValue'
import { WorksheetDetail, WorksheetStatus } from '../../types'
import { canPerformWorksheetAction } from '../../utils/permissions'

type Props = {
  worksheet: WorksheetDetail
  mutateWorksheet: () => void
}

const VisibleStatuses: WorksheetStatus[] = ['deReceived']
const MAX_REASON_LENGTH = 255

export const WithdrawWorksheet: React.FC<Props> = ({
  worksheet,
  mutateWorksheet,
}) => {
  const { isChanged } = useWorksheetValueContext()
  const {
    account: { member: currentMember, selectedTrialHospitalRole },
  } = useAuthenticatedAccount()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useMirohaToast()
  const [reason, setReason] = useState('')

  const isReasonTooLong = reason.length > MAX_REASON_LENGTH
  const isReasonEmpty = !reason.trim()

  const { request: withdrawWorksheet } = useWithdrawWorksheet({
    onSuccess: () => {
      mutateWorksheet()
      onClose()
      setReason('')
      toast({
        status: 'success',
        title: 'ワークシートを差し戻しました',
      })
    },
    onError: error => {
      toast({
        status: 'error',
        title: error.message,
      })
    },
  })

  if (
    !canPerformWorksheetAction(
      'deWithdraw',
      currentMember.role,
      selectedTrialHospitalRole,
    )
  ) {
    return null
  }

  if (!VisibleStatuses.includes(worksheet.status)) {
    return null
  }

  return (
    <>
      <Button
        colorScheme="red"
        isDisabled={isChanged || worksheet.status !== 'deReceived'}
        onClick={onOpen}
      >
        受領取り下げ
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose()
          setReason('')
        }}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>
            <Text fontSize="xl" fontWeight="bold">
              指示の受領を取り下げますか？
            </Text>
          </ModalHeader>
          <ModalBody pt={1}>
            <FormControl isInvalid={isReasonTooLong}>
              <Textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="理由を入力してください"
                rows={4}
              />
              {!isReasonTooLong && (
                <FormHelperText textAlign="right">
                  {reason.length}/{MAX_REASON_LENGTH}文字
                </FormHelperText>
              )}
              <FormErrorMessage>
                理由は{MAX_REASON_LENGTH}文字以内で入力してください
              </FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <ModalCancelButton />
            <Button
              colorScheme="red"
              isDisabled={isReasonEmpty || isReasonTooLong}
              onClick={async () => {
                await withdrawWorksheet({
                  worksheetUid: worksheet.uid,
                  reason: reason.trim(),
                })
              }}
            >
              受領を取り下げる
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
